import React, { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useFinePointer } from "../hooks/useFinePointer";
import { POINTER, POINTER_EVENT } from "../utils/pointer";

const HOVER_SELECTOR = [
  "a",
  "button",
  "summary",
  "input",
  "textarea",
  "select",
  "label",
  "[role='button']",
  "[data-cursor='hover']",
].join(",");

const HOTSPOT = {
  [POINTER.GRAB]: [9, 9],
  [POINTER.GRABBING]: [9, 9],
  [POINTER.HOVER]: [6, 2],
  [POINTER.ZOOM_IN]: [11, 11],
  [POINTER.ZOOM_OUT]: [11, 11],
};

const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "summary",
  "input",
  "textarea",
  "select",
  "label",
  "[role='button']",
].join(",");

const resolveMode = (cameraMode, target, pressing) => {
  if (cameraMode === POINTER.GRABBING) return POINTER.GRABBING;
  if (cameraMode === POINTER.ZOOM_IN || cameraMode === POINTER.ZOOM_OUT) return cameraMode;
  if (target && target.closest && target.closest(INTERACTIVE_SELECTOR)) return POINTER.HOVER;
  if (target && target.closest && target.closest(HOVER_SELECTOR)) return POINTER.HOVER;
  if (pressing) return POINTER.GRABBING;
  return POINTER.GRAB;
};

const CustomCursor = () => {
  const enabled = useFinePointer();
  const cursorRef = useRef(null);

  useLayoutEffect(() => {
    if (!enabled) return undefined;

    const root = document.documentElement;
    root.classList.add("has-custom-cursor");

    const cursor = cursorRef.current;
    if (!cursor) return undefined;

    document.querySelectorAll(".custom-cursor").forEach((node) => {
      if (node !== cursor) node.remove();
    });

    const pos = { x: -100, y: -100, mode: POINTER.GRAB, visible: false, pressing: false };
    let paintRaf = 0;

    const paint = () => {
      paintRaf = 0;
      const [hx, hy] = HOTSPOT[pos.mode] || [0, 0];
      cursor.style.transform = `translate3d(${pos.x - hx}px, ${pos.y - hy}px, 0)`;
      cursor.dataset.mode = pos.mode;
      cursor.classList.toggle("is-visible", pos.visible);
      cursor.classList.toggle("is-press", pos.pressing && pos.mode !== POINTER.GRABBING);
    };

    const requestPaint = () => {
      if (paintRaf) return;
      paintRaf = requestAnimationFrame(paint);
    };

    const applyMode = (target) => {
      const cameraMode = root.dataset.boardPointer || POINTER.GRAB;
      if (cameraMode === POINTER.GRABBING) {
        if (pos.mode !== POINTER.GRABBING) pos.mode = POINTER.GRABBING;
        requestPaint();
        return;
      }
      const next = resolveMode(cameraMode, target, pos.pressing);
      if (next !== pos.mode) pos.mode = next;
      requestPaint();
    };

    const onMove = (event) => {
      pos.x = event.clientX;
      pos.y = event.clientY;
      pos.visible = true;
      const cameraMode = root.dataset.boardPointer || POINTER.GRAB;
      if (cameraMode === POINTER.GRABBING) {
        if (pos.mode !== POINTER.GRABBING) pos.mode = POINTER.GRABBING;
        requestPaint();
        return;
      }
      applyMode(event.target);
    };

    const onOver = (event) => {
      if (root.dataset.boardPointer === POINTER.GRABBING) return;
      applyMode(event.target);
    };

    const onDown = (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      pos.pressing = true;
      applyMode(event.target);
    };

    const onUp = (event) => {
      pos.pressing = false;
      applyMode(event.target);
    };

    const onLeave = () => {
      pos.visible = false;
      pos.pressing = false;
      requestPaint();
    };

    const onBoardPointer = (event) => {
      const cameraMode = event.detail || root.dataset.boardPointer || POINTER.GRAB;
      if (cameraMode === POINTER.GRABBING) {
        pos.mode = POINTER.GRABBING;
        requestPaint();
        return;
      }
      pos.mode = resolveMode(cameraMode, document.elementFromPoint(pos.x, pos.y), pos.pressing);
      requestPaint();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerover", onOver, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener(POINTER_EVENT, onBoardPointer);
    document.addEventListener("mouseleave", onLeave);
    paint();

    return () => {
      root.classList.remove("has-custom-cursor");
      if (paintRaf) cancelAnimationFrame(paintRaf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerover", onOver);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener(POINTER_EVENT, onBoardPointer);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [enabled]);

  if (!enabled) return null;

  return createPortal(
    <div ref={cursorRef} className="custom-cursor" data-mode={POINTER.GRAB} aria-hidden="true">
      <svg className="custom-cursor-icon" data-icon={POINTER.HOVER} viewBox="0 0 24 24" width="24" height="24">
        <path
          d="M5.2 3.1 19.4 14.2l-6.4.2 3.6 7.4-2.7 1.3-3.6-7.3-4.9 4.8V3.1Z"
          fill="#1c1612"
          stroke="#fbf6e8"
          strokeWidth="1.35"
          strokeLinejoin="round"
        />
        <circle cx="7.2" cy="7.4" r="1.15" fill="#c41e3a" />
      </svg>
      <svg className="custom-cursor-icon" data-icon={POINTER.GRAB} viewBox="0 0 24 24" width="24" height="24">
        <path
          d="M8 11.2V6.4a1.4 1.4 0 0 1 2.8 0V11M10.8 11V5.6a1.4 1.4 0 0 1 2.8 0V11M13.6 11.1V7.1a1.4 1.4 0 0 1 2.8 0v6.2M7.9 11.2A1.5 1.5 0 0 0 5.2 12.6c0 3.4 2.2 6.9 6.8 6.9 4.2 0 6.8-3 6.8-6.6V10a1.4 1.4 0 0 0-2.8 0"
          fill="#fbf6e8"
          stroke="#1c1612"
          strokeWidth="1.55"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg className="custom-cursor-icon" data-icon={POINTER.GRABBING} viewBox="0 0 24 24" width="24" height="24">
        <path
          d="M8.1 12.2V10a1.15 1.15 0 0 1 2.3 0v1.8M10.4 12V9.6a1.15 1.15 0 1 1 2.3 0V12M12.7 12v-1.7a1.15 1.15 0 1 1 2.3 0V12.4M7.9 12.4c-.9.3-1.9 1.2-1.9 2.5 0 2.8 2.1 5.3 6 5.3 3.6 0 5.9-2.3 5.9-5.1v-2.4a1.15 1.15 0 0 0-2.3 0"
          fill="#fbf6e8"
          stroke="#1c1612"
          strokeWidth="1.55"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg className="custom-cursor-icon" data-icon={POINTER.ZOOM_IN} viewBox="0 0 24 24" width="24" height="24">
        <circle cx="11" cy="11" r="6.2" fill="#fbf6e8" stroke="#1c1612" strokeWidth="1.6" />
        <path d="M11 8.4v5.2M8.4 11h5.2M15.6 15.6 20 20" stroke="#1c1612" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="11" cy="11" r="1.05" fill="#c41e3a" />
      </svg>
      <svg className="custom-cursor-icon" data-icon={POINTER.ZOOM_OUT} viewBox="0 0 24 24" width="24" height="24">
        <circle cx="11" cy="11" r="6.2" fill="#fbf6e8" stroke="#1c1612" strokeWidth="1.6" />
        <path d="M8.4 11h5.2M15.6 15.6 20 20" stroke="#1c1612" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </div>,
    document.body
  );
};

export default CustomCursor;
